import styled from "styled-components";
import {
  Flex,
  grayText,
  primaryColorDark,
  secondaryColor,
} from "./Utilities.styled";

const Container = styled.div`
  position: relative;
  width: 100%;
`;

const Items = styled.div`
  position: relative;
  box-sizing: border-box;
  width: 100%;
  display: flex;
  gap: 2rem;
  /* Center the cards when they all fit; "safe" keeps them start-aligned (and scrollable) when they overflow */
  justify-content: safe center;
  overflow-x: scroll;
  z-index: auto;
  /* Room for the arrows at each end. The scroll padding adds the cards' 0.8rem margin, so paging and snapping line up with the first card */
  padding-inline: 8rem;
  scroll-padding-inline: 8.8rem;

  &::-webkit-scrollbar-thumb,
  &::-webkit-scrollbar-track {
    background: transparent !important;
    box-shadow: none;
    border-radius: 0;
  }

  &::-webkit-scrollbar {
    width: 0 !important;
  }

  @media (max-width: 568px) {
    padding-inline: 2rem;
    scroll-padding-inline: 2.8rem;
    scroll-snap-type: x proximity;
  }
`;

const ItemImage = styled.div`
  height: auto;
  transform: translateZ(0);
  display: block;
  transition: transform 750ms cubic-bezier(0.2, 1, 0.3, 1);

  &:before {
    content: "";
    display: block;
    padding-top: 75%;
    overflow: hidden;
  }

  img {
    position: absolute;
    top: 0.5rem;
    left: 50%;
    transform: translateX(-50%);
    width: 95%;
    height: 100%;
    line-height: 0;
    object-fit: cover;
  }
`;

const ItemTagsWrap = styled(Flex)`
  flex-wrap: wrap;
  gap: 1rem;
  justify-content: center;
  opacity: 0;
  z-index: 3;
  position: absolute;
  width: 100%;
  top: 3rem;
`;

const Item = styled.div`
  /* Shrink on very small phones so a whole card always fits between the edges */
  width: min(34.6rem, calc(100vw - 5.6rem));
  position: relative;
  overflow: hidden;
  margin: 0.8rem;
  border-radius: 0.8rem;
  flex-shrink: 0;
  display: flex;
  background: ${secondaryColor};
  z-index: 0;

  /* Cards in a row share the tallest card's height, with the links pinned to the bottom */
  .ant-card {
    display: flex;
    flex-direction: column;
    width: 100%;
  }

  .ant-card-body {
    flex: 1;
  }

  &:hover {
    z-index: 1;
    transform: scale(1.025);
    transition: transform 0.5s;
  }

  &::after {
    content: "";
    position: absolute;
    display: block;
    background: inherit;
    opacity: 0.9;
    top: 0;
    left: 0;
    width: 100%;
    height: 18rem;
    transform: scale(2) translateX(-75%) translateY(-75%);
    transition: transform 1s cubic-bezier(0.2, 1, 0.3, 1);
  }

  &:hover::after {
    transform: scale(2) translateX(0) translateY(0);
  }

  &:hover ${ItemImage} {
    transform: scale(0.9);
  }

  &:hover ${ItemTagsWrap} {
    opacity: 1;
    animation-name: moveInLeft;
    -webkit-animation-duration: 1s;
    animation-duration: 1s;
    -webkit-animation-timing-function: ease-out;
    animation-timing-function: ease-out;
  }

  a .anticon {
    color: ${grayText};
    font-size: 2rem;
  }

  @media (max-width: 568px) {
    scroll-snap-align: start;
  }
`;

const ItemDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
  font-size: 1.4rem;
  line-height: 1.55;
  color: #475569;

  /* The global styles make paragraphs white for the dark page; cards are light */
  p {
    color: inherit;
  }
`;

const ItemSubtitle = styled.span`
  font-size: 1.3rem;
  font-weight: 600;
  color: ${primaryColorDark};
`;

const ItemHighlights = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  padding-left: 1.8rem;

  li::marker {
    color: ${primaryColorDark};
  }
`;

const ScrollButton = styled.button`
  color: inherit;
  border: none;
  padding: 0 1rem;
  font: inherit;
  cursor: pointer;
  outline: inherit;
  position: absolute;
  top: 0.8rem;
  bottom: 0.8rem;
  font-size: 5rem;
  color: #fff;
  background-color: rgba(0, 0, 0, 0.4);
  z-index: 3;
  display: ${({ $visible }) => ($visible ? "block" : "none")};

  &:hover {
    color: ${primaryColorDark};
  }

  /* Small round arrows over the card edges on phones */
  @media (max-width: 568px) {
    top: 50%;
    bottom: auto;
    transform: translateY(-50%);
    width: 4.4rem;
    height: 4.4rem;
    padding: 0;
    font-size: 2rem;
    border-radius: 50% !important;
    background-color: rgba(0, 0, 0, 0.6);
  }
`;

const LeftScrollButton = styled(ScrollButton)`
  border-top-right-radius: 0.5rem;
  border-bottom-right-radius: 0.5rem;
  left: 0;

  @media (max-width: 568px) {
    left: 0.4rem;
  }
`;

const RightScrollButton = styled(ScrollButton)`
  border-top-left-radius: 0.5rem;
  border-bottom-left-radius: 0.5rem;
  right: 0;

  @media (max-width: 568px) {
    right: 0.4rem;
  }
`;

export {
  Container,
  Items,
  ItemImage,
  ItemTagsWrap,
  Item,
  ItemDetails,
  ItemSubtitle,
  ItemHighlights,
  LeftScrollButton,
  RightScrollButton,
};
